import {
  Box,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Portal,
  Skeleton,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import type { KeyboardEvent, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { FiPlus } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { Tooltip } from "../components/Tooltip";
import type { SubAccess } from "../subscription/useSub";
import type { AppRole, NavSection, NavSectionAction, SectionActionKind } from "./navConfig";
import { isNavItemActive } from "./navActive";
import { NavBadgeCount } from "./SidebarNavItem";
import { sectionView, type SectionView } from "./sectionView";
import type { NavBadges } from "./useNavBadges";
import { useNavLabel } from "./useNavLabel";
import { MENU_WIDTH_PX } from "./sidebarMode";

export const SIDEBAR_ICON_WIDTH = `${MENU_WIDTH_PX.icons}px`;

interface Props {
  sections: NavSection[];
  role: AppRole;
  access: SubAccess;
  badges: NavBadges;
  activeSectionKey: string | undefined;
  onNavigate: (path: string, query?: string) => void;
  onAction: (action: NavSectionAction) => void;
  pendingActionKind: SectionActionKind | null;
  regionId?: string;
  navRef?: RefObject<HTMLDivElement>;
  resolving?: boolean;
}

export const SidebarIconRail = ({
  sections,
  role,
  access,
  badges,
  activeSectionKey,
  onNavigate,
  onAction,
  pendingActionKind,
  regionId,
  navRef,
  resolving = false,
}: Props) => {
  const { t } = useTranslation();
  return (
    <VStack
      as="nav"
      ref={navRef}
      id={regionId}
      tabIndex={-1}
      aria-label={t("Main navigation") ?? undefined}
      outline="none"
      flex="1"
      align="center"
      spacing="1"
      py="2"
      overflowY="auto"
    >
      {resolving ? (
        <VStack spacing="1" aria-busy="true" aria-label={t("Loading the menu") ?? undefined}>
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} w="48px" h="48px" borderRadius="md" />
          ))}
        </VStack>
      ) : sections.map((section) => {
        const view = sectionView(section, role, access, badges);
        if (!view) return null;
        return (
          <SectionIcon
            key={section.titleKey}
            section={section}
            view={view}
            badges={badges}
            isActive={section.titleKey === activeSectionKey}
            onNavigate={onNavigate}
            onAction={onAction}
            pendingActionKind={pendingActionKind}
          />
        );
      })}
    </VStack>
  );
};

const SectionIcon = ({
  section,
  view,
  badges,
  isActive,
  onNavigate,
  onAction,
  pendingActionKind,
}: {
  section: NavSection;
  view: SectionView;
  badges: NavBadges;
  isActive: boolean;
  onNavigate: (path: string, query?: string) => void;
  onAction: (action: NavSectionAction) => void;
  pendingActionKind: SectionActionKind | null;
}) => {
  const { t } = useTranslation();
  const navLabel = useNavLabel();
  const location = useLocation();
  const menu = useDisclosure();
  const name = navLabel(section.titleKey);
  const label = view.badgeSum > 0 ? t("{{section}} · {{count}} waiting", { section: name, count: view.badgeSum }) : name;
  const look = {
    variant: "ghost",
    size: "lg",
    w: "48px",
    h: "48px",
    color: isActive ? "smPrimary" : "smMuted",
    bg: isActive ? "smPrimarySofter" : undefined,
    _hover: { bg: isActive ? "smPrimarySofter" : "smBackgroundSecondary", color: "smPrimary" },
    "aria-label": label,
    "aria-current": isActive ? ("true" as const) : undefined,
    icon: <Icon as={section.icon} boxSize="5" />,
  };
  const badge = view.badgeSum > 0 && (
    <Box position="absolute" top="1" right="1" pointerEvents="none">
      <NavBadgeCount value={view.badgeSum} isActive={isActive} size="sm" />
    </Box>
  );

  if (view.opensDirectly) {
    const item = view.opensDirectly;
    return (
      <Box position="relative">
        <Tooltip label={label} placement="right">
          <IconButton {...look} onClick={() => onNavigate(item.path, item.query)} />
        </Tooltip>
        {badge}
      </Box>
    );
  }

  const openOnArrowRight = (event: KeyboardEvent) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      menu.onOpen();
    }
  };

  return (
    <Box position="relative">
      <Menu placement="right-start" isOpen={menu.isOpen} onOpen={menu.onOpen} onClose={menu.onClose} isLazy>
        <Tooltip label={label} placement="right" isDisabled={menu.isOpen}>
          <MenuButton as={IconButton} {...look} onKeyDown={openOnArrowRight} />
        </Tooltip>
        <Portal>
          <MenuList minW="240px" zIndex="popover">
            <MenuGroup title={name}>
              {view.items.map((item) => {
                const itemActive = isNavItemActive(item, location.pathname, location.search, view.items);
                const count = item.badgeKey ? badges[item.badgeKey] : undefined;
                return (
                  <MenuItem
                    key={`${item.path}?${item.query ?? ""}:${item.labelKey}`}
                    onClick={() => onNavigate(item.path, item.query)}
                    icon={<Icon as={item.icon} />}
                    aria-current={itemActive ? "page" : undefined}
                    color={itemActive ? "smPrimary" : undefined}
                    fontWeight={itemActive ? "semibold" : undefined}
                    bg={itemActive ? "smPrimarySofter" : undefined}
                  >
                    <Flex justify="space-between" align="center" gap="3">
                      <Text>{navLabel(item.labelKey)}</Text>
                      {count ? <NavBadgeCount value={count} isActive={itemActive} /> : null}
                    </Flex>
                  </MenuItem>
                );
              })}
            </MenuGroup>
            {view.action && (
              <>
                <MenuDivider />
                <MenuItem
                  icon={<Icon as={FiPlus} />}
                  color="smPrimary"
                  fontWeight="semibold"
                  isDisabled={pendingActionKind === view.action.kind}
                  onClick={() => view.action && onAction(view.action)}
                >
                  {navLabel(view.action.labelKey)}
                </MenuItem>
              </>
            )}
          </MenuList>
        </Portal>
      </Menu>
      {badge}
    </Box>
  );
};
